import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatRupiah, priceInfo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Upload, Eye, Truck, ShieldCheck, ArrowRight, Sparkles, CheckCircle2, Flame, MessageCircle } from "lucide-react";

const WA_PHONE = "6282227132011";
const WA_MESSAGE = "Halo, saya ingin konsultasi mengenai cetak kustom di Rooftop Print";

const steps = [
  { icon: Upload, title: "Unggah Desain", desc: "Pilih produk lalu unggah foto atau desainmu." },
  { icon: Eye, title: "Pilih Varian", desc: "Pilih varian, jumlah, dan unggah desain (opsional)." },
  { icon: Truck, title: "Isi Pengiriman", desc: "Ongkir otomatis dihitung ke seluruh Indonesia." },
  { icon: ShieldCheck, title: "Bayar Aman", desc: "Bayar via QRIS, konfirmasi cepat via WhatsApp." },
];

function BannerSlider({ banners }) {
  const [idx, setIdx] = useState(0);
  const list = banners?.length ? banners : [{ id: "d", image_url: "https://images.unsplash.com/photo-1587522384446-64daf3e2689a?w=1200" }];
  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 4500);
    return () => clearInterval(t);
  }, [list.length]);
  return (
    <div className="relative" data-testid="hero-banner-slider">
      <div className="absolute -top-8 -right-8 w-64 h-64 rounded-full bg-[#F0005A]/10 blur-3xl" />
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgb(10,25,47,0.12)]">
        {list.map((b, i) => (
          <img key={b.id || i} src={b.image_url} alt="Banner promo"
            className={`w-full h-[460px] object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 absolute inset-0"}`} />
        ))}
      </div>
      {list.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {list.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`banner-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-2 bg-white/60"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [sf, setSf] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings/storefront").then((r) => setSf(r.data)).catch(() => {});
    api.get("/settings/clients").then((r) => setClients(r.data?.clients || [])).catch(() => {});
  }, []);

  const hero = sf?.hero || {};
  const bestSellers = products.filter((p) => p.best_seller).slice(0, 4);
  const bestList = bestSellers.length ? bestSellers : products.slice(0, 4);
  const waHref = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="App">
      <Navbar />

      {/* Hero / Banner */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F0005A]/10 text-[#F0005A] text-xs font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Produk custom & ready, harga terjangkau
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#00133F] leading-[1.05]">
              {hero.title || <>Cetak <span className="text-[#F0005A]">mug & plakat</span> custom sesuai imajinasimu.</>}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              data-testid="hero-subtitle"
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg whitespace-pre-line">
              {hero.subtitle || "Unggah desain, pilih varian, hitung ongkir otomatis, dan bayar dengan QRIS. Simpel dan profesional."}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="mt-8 flex flex-wrap gap-3">
              <Link to="/produk">
                <Button data-testid="hero-cta-btn" className="rounded-full h-12 px-7 text-base bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white">
                  {hero.cta_text || "Mulai Desain"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#cara-kerja">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base border-slate-300">Cara Kerja</Button>
              </a>
            </motion.div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              {["Kualitas cetak HD", "Kirim seluruh Indonesia", "Layanan cepat"].map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> {t}
                </div>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
            <BannerSlider banners={sf?.banners} />
          </motion.div>
        </div>
      </section>

      {/* Produk Terlaris / Best Seller */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[#F0005A] text-sm font-semibold uppercase tracking-wider mb-1">
              <Flame className="w-4 h-4" /> Produk Terlaris
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#00133F]">Best Seller pilihan pelanggan</h2>
          </div>
          <Link to="/produk" className="hidden sm:inline-flex items-center text-sm font-medium text-[#F0005A] hover:underline">
            Lihat semua <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {bestList.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Link to={`/kustom/${p.id}`} data-testid={`bestseller-${p.type}`}
                className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-[0_20px_50px_rgb(10,25,47,0.12)] hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
                <div className="aspect-square overflow-hidden bg-slate-50 relative">
                  {p.best_seller && (
                    <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-[#F0005A] text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      <Flame className="w-3 h-3" /> BEST SELLER
                    </span>
                  )}
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-lg font-semibold text-[#00133F]">{p.name}</h3>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#F0005A] font-bold">{priceInfo(p).from ? "mulai " : ""}{formatRupiah(priceInfo(p).effective)}</span>
                      {priceInfo(p).promo != null && <span className="text-xs text-slate-400 line-through">{formatRupiah(p.base_price)}</span>}
                      {priceInfo(p).discount > 0 && <span className="text-[10px] font-bold text-[#F0005A] bg-[#F0005A]/10 px-1.5 py-0.5 rounded-full">Hemat {priceInfo(p).discount}%</span>}
                    </div>
                    <span className="text-xs text-slate-400">/ pcs</span>
                  </div>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-[#00133F] group-hover:text-[#F0005A] transition-colors">
                    Kustomisasi <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="cara-kerja" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">Alur Pemesanan</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#00133F]">Empat langkah mudah</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="relative rounded-2xl border border-slate-200 bg-white p-6">
              <div className="w-11 h-11 rounded-xl bg-[#00133F] flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-[#F0005A]" />
              </div>
              <span className="absolute top-6 right-6 font-heading text-4xl font-bold text-slate-100">0{i + 1}</span>
              <h3 className="font-heading text-lg font-semibold text-[#00133F]">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="rounded-2xl bg-[#00133F] p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-[#F0005A]/20 blur-3xl" />
          <h2 className="relative font-heading text-3xl sm:text-4xl font-bold text-white mb-4">Siap wujudkan desainmu?</h2>
          <p className="relative text-slate-200 text-base sm:text-lg max-w-xl mx-auto mb-8 leading-relaxed">Dapatkan berbagai produk kustom berkualitas dengan harga terjangkau. Pesan mudah, tanpa ribet.</p>
          <Link to="/produk" className="relative">
            <Button className="rounded-full h-12 px-8 bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white text-base">
              Mulai Sekarang <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Telah Melayani / Clients */}
      {clients.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="clients-section">
          <div className="text-center mb-10">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">Telah Melayani</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#00133F]">Dipercaya oleh berbagai brand & instansi</h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {clients.map((c, i) => (
              <motion.img key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                src={c.logo_url} alt={c.name} title={c.name}
                className="h-10 sm:h-12 w-auto object-contain grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all" />
            ))}
          </div>
        </section>
      )}

      {/* WhatsApp Consultation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="wa-consult-section">
        <div className="rounded-2xl border border-[#25D366]/30 bg-gradient-to-r from-[#25D366]/10 to-transparent p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#25D366] flex items-center justify-center shrink-0">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#00133F]">Butuh bantuan lebih lanjut?</h3>
              <p className="text-slate-600 mt-1 text-sm sm:text-base">Konsultasi kebutuhan cetak kustom Anda langsung via WhatsApp. Tim kami siap membantu.</p>
            </div>
          </div>
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="wa-consult-btn" className="shrink-0">
            <Button className="rounded-full h-12 px-7 text-base bg-[#25D366] hover:brightness-110 active:scale-95 transition-transform text-white">
              <MessageCircle className="w-5 h-5 mr-2" /> Konsultasi via WhatsApp
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
